/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { FormikProps } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { OrderType } from 'models/enums'
import { SignUpFields } from 'models/app'
import { Address } from 'models/account'
import { PendingItem } from 'models/shop'
import { clearItems, setCartItem, setCartOpen, setCartUpdateStatus } from 'store/slices/shopSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { DomainContact } from 'helpers/utils'

import CartForm from 'containers/shop/cart/form/cartForm'
import ContactDetailsForm from 'containers/shop/domains/form/contactDetailsForm'
import AccountSelector from 'containers/shop/cart/accountSelector'
import SignupForm from 'containers/shop/domains/form/signupForm'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Cart() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const formRef = useRef<FormikProps<Address>>(null)
    const signupFormRef = useRef<FormikProps<SignUpFields>>(null)
    const { appSession: { authenticated } } = useAppSelector((state) => state.app)
    const {
        cartOpen,
        cartItem,
        cartStatus,
        cartUpdateStatus,
        items,
        cart
    } = useAppSelector((state) => state.shop)
    const domainsHaveContact = items.filter(({ product_type, order_type }: PendingItem) => {
        return product_type === 'domain' && order_type === OrderType.Register
    }).every((domain: PendingItem) => {
        return domain.domain_contact_fields
    })
    const [updateCartItems, { isSuccess }] = useUpdateCartItemsMutation()
    let conditionalProps
    const links = !cart || cartStatus === 'loading'
        ? []
        : [
            {
                label: 'Clear Cart',
                color: 'error',
                disabled: cartUpdateStatus === 'loading',
                onClick: () => dispatch(setCartItem({ ...cartItem, template: 'clear' }))
            }
        ]

    function updateOrCheckout() {
        if (!authenticated) {
            dispatch(setCartItem({ ...cartItem, template: 'authentication status' }))
        } else {
            dispatch(setCartOpen(false))
            navigate('/checkout')
        }
    }

    useEffect(() => {
        if (isSuccess && cartItem.template === 'clear') {
            dispatch(setCartItem({ template: 'clear', item: {} }))
        }

        if (cartUpdateStatus === 'success' && cartItem.template === 'contact' && formRef.current && formRef.current.isSubmitting) {
            dispatch(setCartItem({ template: 'cart' }))
            dispatch(setCartUpdateStatus('idle'))
        }

        if (cartUpdateStatus === 'success' && !cart) {
            dispatch(setCartOpen(false))
            dispatch(setCartItem({ items: {}, template: 'cart' }))
        }
    }, [cart, cartItem.template, cartUpdateStatus, dispatch, isSuccess, items.length])

    switch (cartItem.template) {
        case 'clear':
            conditionalProps = {
                title: 'Clear Cart',
                type: 'confirm',
                icon: 'warning',
                description: 'Are you sure you would like to remove all configured domain name and service items from the cart?',
                loading: cartStatus === 'loading' || cartUpdateStatus === 'loading',
                conditions: { state: cartOpen, action: () => dispatch(setCartOpen(false)), animate: true },
                actions: [
                    {
                        label: 'No, keep all items',
                        color: 'primary',
                        type: 'button',
                        func: () => dispatch(setCartItem({ ...cartItem, template: 'cart' }))
                    },
                    {
                        label: 'Clear Cart',
                        color: 'error',
                        disabled: !cart || cartStatus === 'loading' || cartUpdateStatus === 'loading' || cartStatus === 'error',
                        type: 'submit',
                        func: () => {
                            if (cart?.uuid) {
                                updateCartItems({
                                    uuid: cart.uuid,
                                    remove_items: cart.items.map(({ item_uuid }) => item_uuid)
                                })
                                dispatch(clearItems())
                            }
                        }
                    }
                ]
            }
            break
        case 'contact':
            conditionalProps = {
                title: 'Domain Contact Details',
                content: (
                    <ContactDetailsForm
                        domain={cartItem.item}
                        form={formRef}
                        prefillValues={cartItem.item.domain_contact_fields ? cartItem.item.domain_contact_fields : DomainContact}
                        mode={'editing'}
                        close={() => dispatch(setCartOpen(false))}
                    />
                ),
                conditions: { state: cartOpen, action: () => dispatch(setCartOpen(false)) },
                loading: cartStatus === 'loading',
                actions: [
                    {
                        label: 'Back',
                        color: 'flat',
                        type: 'button',
                        func: () => {
                            if (pathname === '/checkout') {
                                dispatch(setCartOpen(false))
                            } else {
                                dispatch(setCartItem({ ...cartItem, template: 'cart' }))
                            }
                        }
                    },
                    {
                        label: 'Update',
                        color: 'primary',
                        disabled: cartUpdateStatus === 'loading',
                        type: 'submit',
                        form: 'contactDetailsForm'
                    }
                ]
            }
            break
        case 'authentication status':
            conditionalProps = {
                title: 'User Account',
                content: <AccountSelector />,
                conditions: { state: cartOpen, action: () => dispatch(setCartOpen(false)) },
                links: [{
                    label: 'Return to Cart',
                    onClick: () => dispatch(setCartItem({ ...cartItem, template: 'cart' }))
                }]
            }
            break
        case 'signup':
            conditionalProps = {
                title: 'Signup',
                content: <SignupForm form={signupFormRef} />,
                conditions: { state: cartOpen, action: () => dispatch(setCartOpen(false)) },
                actions: [
                    {
                        label: 'Back',
                        color: 'flat',
                        type: 'button',
                        func: () => dispatch(setCartItem({ ...cartItem, template: 'cart' }))
                    },
                    {
                        label: 'Sign Up and Checkout',
                        color: 'primary',
                        type: 'submit',
                        form: 'signupForm'
                    }
                ]
            }
            break
        case 'cart':
        default:
            conditionalProps = {
                links,
                title: 'Cart',
                content: <CartForm />,
                loading: cartStatus === 'loading',
                conditions: { state: cartOpen, action: () => dispatch(setCartOpen(false)) },
                actions: [
                    {
                        label: 'Back',
                        color: 'flat',
                        type: 'button',
                        func: () => dispatch(setCartOpen(false))
                    },
                    {
                        label: 'Checkout',
                        color: 'primary',
                        disabled: !cart || cartStatus === 'loading' || cartUpdateStatus === 'loading' || cartStatus === 'error' || !domainsHaveContact,
                        type: 'submit',
                        func: updateOrCheckout
                    }
                ]
            }
    }

    return <Lightbox {...conditionalProps} close={() => dispatch(setCartOpen(false))}/>
}
